import { v4 as uuid } from 'uuid';

import {
  Mapping,
  MappingIdentifier,
  MappingLabel,
} from 'src/containers/ProjectDiagram/types/data';
import { Diagram } from 'src/containers/ProjectDiagram/types/diagram';
import { Model, ModelData } from 'src/containers/ProjectDiagram/types/model';
import { generateDetachedKey } from 'src/containers/ProjectDiagram/utils/detachedKey';
import { fixUnconnectedLinkPoints } from 'src/containers/ProjectDiagram/utils/fixUnconnectedLinkPoints';
import {
  createMapping,
  getMappingKey,
} from 'src/containers/ProjectDiagram/utils/mapping/createMapping';
import {
  createMappingLabel,
  getMappingLabelKey,
} from 'src/containers/ProjectDiagram/utils/mapping/createMappingLabel';
import { setDataProperty } from 'src/containers/ProjectDiagram/utils/setDataProperty';
import * as copy from 'src/templates/copy';

export const addMapping =
  ({
    fromId,
    value = uuid(),
    toKey,
    label,
    fields,
    primaryIdentifier,
    text: providedText,
  }: Pick<Mapping, 'fields'> & {
    fromId: string;
    label?: string | null;
    value?: string;
    toKey?: string;
    primaryIdentifier?: MappingIdentifier;
    text?: string;
  }) =>
  (model: Model, diagram: Diagram) => {
    const key = getMappingKey({ fromId, value, toKey });
    const existingLinkData = model.linkDataArray.find(
      (link) => link.key === key
    );

    if (existingLinkData) {
      return { mapping: undefined, mappingLabel: undefined };
    }

    let newMapping: Mapping;
    let newMappingLabel: MappingLabel;

    const text =
      providedText ||
      copy.components.projectDiagram.mapping.defaultLabel(
        model.modelData.mappingIdx
      );

    if (toKey) {
      newMapping = createMapping({
        label,
        fields,
        fromId,
        value,
        toKey,
        text,
        primaryIdentifier,
      });
      newMappingLabel = createMappingLabel({ fromId, value, toKey, text });
    } else {
      const toKey = generateDetachedKey();
      const keyOverride = getMappingKey({
        fromId,
        toKey,
        value,
      });
      const labelKeyOverride = getMappingLabelKey({
        fromId,
        toKey,
        value,
      });

      newMapping = createMapping({
        fromId,
        value,
        text,
        label,
        fields,
        primaryIdentifier,
        keyOverride,
        labelKeyOverride,
      });
      newMappingLabel = createMappingLabel({
        fromId,
        value,
        text,
        keyOverride: labelKeyOverride,
      });
    }

    setDataProperty<ModelData>(model)(
      model.modelData,
      'mappingIdx',
      model.modelData.mappingIdx + 1
    );
    model.addLinkData(newMapping);
    model.addNodeData(newMappingLabel);

    fixUnconnectedLinkPoints(newMapping)(model, diagram);

    return { mapping: newMapping, mappingLabel: newMappingLabel };
  };
